import React, { useState, useEffect, useCallback, useMemo } from 'react';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';
import api from '../components/api';
import '../css/BorstweringenInventarisatieAdmin.css';
import '../css/PageWithSidebar.css';

const STATUS_LABELS = {
  gedaan: { label: 'Gedaan', className: 'status-gedaan', icon: '✅' },
  gedeeltelijk: { label: 'Gedeeltelijk', className: 'status-gedeeltelijk', icon: '🟡' },
  niet: { label: 'Niet gedaan', className: 'status-niet', icon: '❌' },
  none: { label: 'Niet gereageerd', className: 'status-none', icon: '—' }
};

const FILTERS = [
  { value: 'all', label: 'Alles' },
  { value: 'none', label: 'Niet gereageerd' },
  { value: 'gedaan', label: 'Gedaan' },
  { value: 'gedeeltelijk', label: 'Gedeeltelijk' },
  { value: 'niet', label: 'Niet gedaan' }
];

const BorstweringenInventarisatieAdmin = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/api/borstweringen-inventarisatie/admin/all');
      setData(res.data);
    } catch (err) {
      console.error('Error fetching inventarisatie:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Weet u zeker dat u dit antwoord wilt verwijderen?')) return;
    try {
      await api.delete(`/api/borstweringen-inventarisatie/admin/${id}`);
      fetchData();
    } catch (err) {
      alert('Fout bij verwijderen');
    }
  };

  const filteredRows = useMemo(() => {
    if (!data) return [];
    if (filter === 'all') return data.rows;
    if (filter === 'none') return data.rows.filter(r => !r.hasResponded);
    return data.rows.filter(r => r.response && r.response.status === filter);
  }, [data, filter]);

  return (
    <BeheerdersportaalSidebar>
      <div className="inventarisatie-admin">
        <div className="inventarisatie-admin-header">
          <div>
            <h1>Borstweringen Inventarisatie</h1>
            <p className="admin-subtitle">Reacties op de inventarisatie-mail (werk 3-5 april 2026)</p>
          </div>
          {data && data.sheetUrl && (
            <a
              href={data.sheetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-sheet"
            >
              Open Google Sheet
            </a>
          )}
        </div>

        {loading ? (
          <p>Laden...</p>
        ) : !data ? (
          <p>Geen data beschikbaar.</p>
        ) : (
          <>
            <div className="admin-stats">
              <div className="stat-card stat-gedaan">
                <div className="stat-number">{data.stats.gedaan}</div>
                <div className="stat-label">Gedaan</div>
              </div>
              <div className="stat-card stat-gedeeltelijk">
                <div className="stat-number">{data.stats.gedeeltelijk}</div>
                <div className="stat-label">Gedeeltelijk</div>
              </div>
              <div className="stat-card stat-niet">
                <div className="stat-number">{data.stats.niet}</div>
                <div className="stat-label">Niet gedaan</div>
              </div>
              <div className="stat-card stat-none">
                <div className="stat-number">{data.stats.nietGereageerd}</div>
                <div className="stat-label">Niet gereageerd</div>
              </div>
              <div className="stat-card stat-total">
                <div className="stat-number">{data.stats.totaalActief}</div>
                <div className="stat-label">Totaal woningen</div>
              </div>
            </div>

            <div className="filter-bar">
              {FILTERS.map(f => (
                <button
                  key={f.value}
                  type="button"
                  className={`filter-btn ${filter === f.value ? 'active' : ''}`}
                  onClick={() => setFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Huisnr</th>
                    <th>Status</th>
                    <th>Naam (ingediend)</th>
                    <th>Naam (bewonerslijst)</th>
                    <th>Opmerkingen</th>
                    <th>Ingediend op</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map(row => {
                    const statusKey = row.hasResponded ? row.response.status : 'none';
                    const status = STATUS_LABELS[statusKey];
                    return (
                      <tr key={row.huisnummer} className={row.hasResponded ? '' : 'row-no-response'}>
                        <td><strong>{row.huisnummer}</strong></td>
                        <td>
                          <span className={`status-badge ${status.className}`}>
                            <span className="status-badge-icon">{status.icon}</span>
                            {status.label}
                          </span>
                        </td>
                        <td>{row.response ? row.response.naam : '—'}</td>
                        <td>{row.residentNaam || '—'}</td>
                        <td className="cell-opmerkingen">
                          {row.response && row.response.opmerkingen ? row.response.opmerkingen : '—'}
                        </td>
                        <td>
                          {row.response && row.response.submittedAt
                            ? new Date(row.response.submittedAt).toLocaleString('nl-NL')
                            : '—'}
                        </td>
                        <td>
                          {row.response && (
                            <button
                              type="button"
                              className="btn-delete"
                              onClick={() => handleDelete(row.response._id)}
                            >
                              Verwijder
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredRows.length === 0 && (
              <p className="no-results">Geen resultaten voor dit filter.</p>
            )}
          </>
        )}
      </div>
    </BeheerdersportaalSidebar>
  );
};

export default BorstweringenInventarisatieAdmin;
